import { useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Divider,
  IconButton,
  Link,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import {
  InfoOutlined,
  LaunchOutlined,
  MenuBookOutlined,
} from "@mui/icons-material";

/* =========================== ilustrações =========================== */
function IlluQuadrante({ theme }) {
  const { error, success, warning, text } = theme.palette;
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <rect x="166" y="10" width="146" height="40" fill={alpha(error.main, 0.12)} />
      <line x1="20" y1="10" x2="20" y2="90" stroke={text.secondary} strokeWidth="1" />
      <line x1="20" y1="90" x2="312" y2="90" stroke={text.secondary} strokeWidth="1" />
      <line x1="166" y1="10" x2="166" y2="90" stroke={text.secondary} strokeDasharray="3 3" opacity="0.5" />
      <line x1="20" y1="50" x2="312" y2="50" stroke={text.secondary} strokeDasharray="3 3" opacity="0.5" />
      <circle cx="245" cy="30" r="8" fill={alpha(error.main, 0.85)} />
      <circle cx="90" cy="72" r="5" fill={alpha(success.main, 0.85)} />
      <circle cx="255" cy="74" r="6" fill={alpha(warning.main, 0.85)} />
      <circle cx="80" cy="30" r="4" fill={alpha(warning.main, 0.6)} />
      <text x="200" y="24" fontSize="8" fill={error.main} fontWeight="700">prioridade</text>
      <text x="2" y="16" fontSize="8" fill={text.secondary}>% reinc.</text>
      <text x="285" y="99" fontSize="8" fill={text.secondary}>volume</text>
    </svg>
  );
}

function IlluWilson({ theme }) {
  const { error, text } = theme.palette;
  const bar = (y, brutoW, ajusW, label) => (
    <g>
      <text x="2" y={y + 9} fontSize="9" fill={text.secondary}>{label}</text>
      <rect x="60" y={y} width={brutoW} height="11" rx="2" fill={alpha(error.main, 0.25)} />
      <rect x="60" y={y} width={ajusW} height="11" rx="2" fill={error.main} />
    </g>
  );
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      {bar(16, 230, 118, "n=4")}
      <text x="296" y="25" fontSize="8" fill={text.secondary}>100%→51%</text>
      {bar(52, 69, 64, "n=80")}
      <text x="138" y="61" fontSize="8" fill={text.secondary}>30%→28%</text>
      <text x="60" y="86" fontSize="9" fill={text.secondary}>
        ▮ bruto   ▮ ajustado (encolhe quando a amostra é pequena)
      </text>
    </svg>
  );
}

function IlluTmt({ theme }) {
  const { error, primary, text } = theme.palette;
  const pts = [
    [40, 78], [70, 70], [95, 74], [130, 60], [165, 55], [200, 48], [240, 40], [285, 30],
  ];
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line x1="24" y1="10" x2="24" y2="88" stroke={text.secondary} strokeWidth="1" />
      <line x1="24" y1="88" x2="312" y2="88" stroke={text.secondary} strokeWidth="1" />
      <line x1="34" y1="80" x2="295" y2="26" stroke={primary.main} strokeWidth="2" strokeDasharray="5 4" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="4.5" fill={alpha(error.main, 0.75)} />
      ))}
      <text x="2" y="16" fontSize="8" fill={text.secondary}>%</text>
      <text x="280" y="99" fontSize="8" fill={text.secondary}>TMT (h)</text>
      <text x="150" y="20" fontSize="9" fill={primary.main} fontWeight="700">tendência</text>
    </svg>
  );
}

function IlluPareto({ theme }) {
  const { primary, text } = theme.palette;
  // Linha de acumulado em amarelo, igual ao gráfico real do Pareto.
  const acumulado = "#FFC107";
  const bars = [70, 52, 30, 20, 14, 9, 6];
  const cum = [];
  let acc = 0;
  const total = bars.reduce((s, v) => s + v, 0);
  bars.forEach((b) => {
    acc += b;
    cum.push(acc / total);
  });
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line x1="24" y1="88" x2="312" y2="88" stroke={text.secondary} strokeWidth="1" />
      <line x1="24" y1="14" x2="312" y2="14" stroke={text.secondary} strokeDasharray="3 3" opacity="0.4" />
      <text x="290" y="12" fontSize="8" fill={text.secondary}>80%</text>
      {bars.map((b, i) => (
        <rect key={i} x={30 + i * 40} y={88 - b} width="26" height={b} rx="2" fill={alpha(primary.main, 0.8)} />
      ))}
      <polyline
        fill="none"
        stroke={acumulado}
        strokeWidth="2"
        points={cum.map((c, i) => `${43 + i * 40},${88 - c * 74}`).join(" ")}
      />
      {cum.map((c, i) => (
        <circle key={i} cx={43 + i * 40} cy={88 - c * 74} r="2.5" fill={acumulado} />
      ))}
    </svg>
  );
}

function IlluEfeito({ theme }) {
  const { error, info, success, warning, text, background } = theme.palette;
  const dot = (cx, cy, color) => (
    <circle cx={cx} cy={cy} r="5" fill={color} stroke={background.paper} strokeWidth="1" />
  );
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      {/* Rede: agrupado */}
      <text x="2" y="22" fontSize="9" fill={text.secondary}>Rede</text>
      <rect x="44" y="14" width="262" height="14" rx="7" fill={alpha(info.main, 0.08)} />
      {dot(150, 21, success.main)}
      {dot(162, 21, success.main)}
      {dot(140, 21, success.main)}
      {dot(172, 21, warning.main)}
      <line x1="155" y1="12" x2="155" y2="30" stroke={text.primary} strokeWidth="1.5" />
      {/* Pessoa: espalhado */}
      <text x="2" y="62" fontSize="9" fill={text.secondary}>Pessoa</text>
      <rect x="44" y="54" width="262" height="14" rx="7" fill={alpha(error.main, 0.08)} />
      {dot(70, 61, success.main)}
      {dot(130, 61, warning.main)}
      {dot(210, 61, error.main)}
      {dot(285, 61, error.main)}
      <line x1="175" y1="52" x2="175" y2="70" stroke={text.primary} strokeWidth="1.5" />
      <text x="44" y="90" fontSize="8.5" fill={text.secondary}>
        espalhado = varia por técnico • agrupado = é a região
      </text>
    </svg>
  );
}

function IlluCurva({ theme }) {
  const { error, text } = theme.palette;
  const pts = [
    [24, 84], [50, 60], [80, 46], [120, 40], [165, 36], [210, 33], [260, 31], [300, 30],
  ];
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line x1="24" y1="10" x2="24" y2="88" stroke={text.secondary} strokeWidth="1" />
      <line x1="24" y1="88" x2="312" y2="88" stroke={text.secondary} strokeWidth="1" />
      <polyline fill="none" stroke={error.main} strokeWidth="2" points={pts.map((p) => p.join(",")).join(" ")} />
      {[80, 165, 260].map((x, i) => (
        <g key={i}>
          <line x1={x} y1="14" x2={x} y2="88" stroke={text.secondary} strokeDasharray="3 3" opacity="0.4" />
          <text x={x - 6} y="99" fontSize="8" fill={text.secondary}>{[30, 60, 90][i]}d</text>
        </g>
      ))}
      <text x="2" y="16" fontSize="8" fill={text.secondary}>% acum.</text>
    </svg>
  );
}

function IlluSpc({ theme }) {
  const { primary, error, success, text } = theme.palette;
  const pts = [42, 48, 40, 52, 38, 44, 70, 46, 40, 36];
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line x1="24" y1="22" x2="312" y2="22" stroke={error.main} strokeDasharray="5 4" />
      <line x1="24" y1="50" x2="312" y2="50" stroke={text.secondary} opacity="0.6" />
      <line x1="24" y1="78" x2="312" y2="78" stroke={success.main} strokeDasharray="5 4" />
      <text x="2" y="20" fontSize="8" fill={error.main}>UCL</text>
      <text x="2" y="76" fontSize="8" fill={success.main}>LCL</text>
      <polyline
        fill="none"
        stroke={primary.main}
        strokeWidth="2"
        points={pts.map((y, i) => `${36 + i * 28},${y}`).join(" ")}
      />
      {pts.map((y, i) => (
        <circle key={i} cx={36 + i * 28} cy={y} r={y < 22 ? 4.5 : 3} fill={y < 22 ? error.main : primary.main} />
      ))}
    </svg>
  );
}

function IlluSankey({ theme }) {
  const { primary, warning, text } = theme.palette;
  const link = (y1, y2) => (
    <path
      d={`M 88 ${y1} C 160 ${y1}, 160 ${y2}, 232 ${y2}`}
      fill="none"
      stroke={alpha(primary.main, 0.3)}
      strokeWidth="6"
    />
  );
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      {link(28, 26)}
      {link(28, 60)}
      {link(64, 60)}
      {link(64, 84)}
      <rect x="80" y="16" width="8" height="24" rx="2" fill={primary.main} />
      <rect x="80" y="54" width="8" height="22" rx="2" fill={primary.main} />
      <rect x="232" y="16" width="8" height="22" rx="2" fill={warning.main} />
      <rect x="232" y="50" width="8" height="22" rx="2" fill={warning.main} />
      <rect x="232" y="76" width="8" height="14" rx="2" fill={warning.main} />
      <text x="2" y="22" fontSize="8" fill={text.secondary}>abertura</text>
      <text x="252" y="22" fontSize="8" fill={text.secondary}>fechamento</text>
    </svg>
  );
}

/* =========================== conteúdos =========================== */
export const GUIDES = {
  quadrante: {
    title: "Matriz Volume × Reincidência",
    descricao:
      "Cruza o volume de chamados (X) com a % de reincidência (Y); o tamanho da bolha é o nº de reincidências. As linhas tracejadas são as medianas e dividem em 4 quadrantes.",
    Illustration: IlluQuadrante,
    legenda: "Quadrante destacado = alto volume + alta % (prioridade)",
    comoLer: [
      "↗ Alto volume + alta %: prioridade máxima — resolver aqui derruba o indicador geral.",
      "↘ Alto volume + baixa %: referência (benchmark a replicar).",
      "↖ Baixo volume + alta %: provável ruído (poucos casos).",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Diagrama_de_dispers%C3%A3o",
      label: "Diagrama de dispersão",
    },
  },
  wilson: {
    title: "Intervalo de confiança de Wilson (95%)",
    descricao:
      "Ajusta a % de reincidência penalizando amostras pequenas, usando o limite inferior do intervalo de Wilson. Evita que 1 chamado e 100% fique acima de 50 chamados e 30%.",
    Illustration: IlluWilson,
    legenda: "Quanto menor a amostra, mais o valor encolhe para baixo",
    comoLer: [
      "Ranqueia por confiança: o topo é quem temos certeza estatística de que é crítico.",
      "Amostras grandes quase não mudam; pequenas caem bastante.",
    ],
    wiki: {
      url: "https://en.wikipedia.org/wiki/Binomial_proportion_confidence_interval#Wilson_score_interval",
      label: "Wilson score interval (EN)",
    },
  },
  tmt: {
    title: "TMT × Reincidência",
    descricao:
      "Cada ponto é um técnico/atendente: X = TMT (tempo médio de tratamento, em horas), Y = % reincidência, tamanho = volume. A linha tracejada é a regressão linear (tendência).",
    Illustration: IlluTmt,
    legenda: "A inclinação da linha mostra a direção da relação",
    comoLer: [
      "Linha subindo → mais tempo associado a mais reincidência.",
      "Linha descendo → atendimento apressado reincide mais.",
      "Linha plana → o TMT não explica a reincidência.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Regress%C3%A3o_linear",
      label: "Regressão linear",
    },
  },
  pareto: {
    title: "Análise de Pareto (80/20)",
    descricao:
      "Ordena os responsáveis pela maior contribuição à reincidência total. Barras = nº de reincidências (decrescente); linha = % acumulado.",
    Illustration: IlluPareto,
    legenda: "Onde a linha cruza ~80% estão os 'poucos vitais'",
    comoLer: [
      "Os que estão antes do cruzamento de 80% concentram a maior parte do problema.",
      "Atacar esse grupo gera a maior redução absoluta da reincidência.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Princ%C3%ADpio_de_Pareto",
      label: "Princípio de Pareto",
    },
  },
  efeito: {
    title: "Efeito Técnico vs Rede",
    descricao:
      "Por filial, mostra a dispersão da reincidência entre os técnicos (bolinhas), a faixa mín–máx e a média (linha). A classificação usa o coeficiente de variação.",
    Illustration: IlluEfeito,
    legenda: "Espalhado = efeito pessoa • agrupado = efeito rede",
    comoLer: [
      "Bolinhas espalhadas → 'Efeito Pessoa': varia por técnico → treinar/acompanhar.",
      "Bolinhas agrupadas → 'Efeito Rede': todos parecidos → problema é infra/região.",
      "Dispersão intermediária → 'Misto': parte é do técnico, parte é da rede.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Coeficiente_de_varia%C3%A7%C3%A3o",
      label: "Coeficiente de variação",
    },
  },
  curva: {
    title: "Curva de reincidência por dia",
    descricao:
      "Mostra o % acumulado de reincidência do dia 1 ao 90 após o fechamento. As janelas 30/60/90 são apenas 3 fotos desta curva — é uma análise de sobrevivência.",
    Illustration: IlluCurva,
    legenda: "Curva íngreme no início = reparo malfeito; subida no fim = desgaste",
    comoLer: [
      "Subida forte nos primeiros dias → o conserto não resolveu (reparo malfeito).",
      "Subida só lá pelo dia 80 → desgaste natural, problema diferente.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/An%C3%A1lise_de_sobreviv%C3%AAncia",
      label: "Análise de sobrevivência",
    },
  },
  spc: {
    title: "Carta de controle (SPC)",
    descricao:
      "Acompanha o % de reincidência ao longo do tempo contra limites estatísticos (média ± 3 desvios). Separa variação normal de mudança real do processo.",
    Illustration: IlluSpc,
    legenda: "Ponto acima da UCL = sinal real, não ruído",
    comoLer: [
      "Dentro das linhas (UCL/LCL) → variação normal, não reaja.",
      "Ponto fora dos limites → algo mudou de verdade, investigue.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Gr%C3%A1fico_de_controle",
      label: "Gráfico de controle",
    },
  },
  sankey: {
    title: "Fluxo Abertura → Fechamento",
    descricao:
      "Liga o motivo de abertura do chamado ao motivo de fechamento. A largura do fluxo é o volume — revela a causa-raiz real por trás de cada motivo.",
    Illustration: IlluSankey,
    legenda: "Fluxo grosso recorrente = padrão de defeito",
    comoLer: [
      "Quando um motivo de abertura sempre vira o mesmo fechamento, ali está o defeito.",
      "Fluxos largos para 'cancelou/reparo' indicam diagnóstico ou execução falhos.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Diagrama_de_Sankey",
      label: "Diagrama de Sankey",
    },
  },
};

/* =========================== componente =========================== */
export function MetricInfo({ theme, guide }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  if (!guide) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: theme.palette.text.secondary }}
        aria-label="Explicar métrica"
      >
        <InfoOutlined fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { borderRadius: "14px", maxWidth: 380 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.5 }}>
            {guide.title}
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: theme.palette.text.secondary, mb: 1.5 }}
          >
            {guide.descricao}
          </Typography>

          {guide.Illustration && (
            <Box
              sx={{
                borderRadius: "10px",
                backgroundColor: alpha(theme.palette.text.primary, 0.03),
                p: 1,
              }}
            >
              <guide.Illustration theme={theme} />
            </Box>
          )}
          {guide.legenda && (
            <Typography
              sx={{
                fontSize: 11,
                color: theme.palette.text.secondary,
                mt: 0.5,
                mb: 1.5,
                textAlign: "center",
              }}
            >
              {guide.legenda}
            </Typography>
          )}

          {guide.comoLer?.length > 0 && (
            <>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                }}
              >
                Como ler
              </Typography>
              <Stack component="ul" sx={{ m: 0, pl: 2.2, gap: 0.5 }}>
                {guide.comoLer.map((c, i) => (
                  <Typography
                    key={i}
                    component="li"
                    sx={{ fontSize: 13, lineHeight: 1.35 }}
                  >
                    {c}
                  </Typography>
                ))}
              </Stack>
            </>
          )}

          {guide.wiki && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Link
                href={guide.wiki.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <MenuBookOutlined sx={{ fontSize: 16 }} />
                {guide.wiki.label}
                <LaunchOutlined sx={{ fontSize: 14 }} />
              </Link>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
